import React, { useEffect, useState } from 'react';
import { Tooltip, Button } from 'antd';
import { broadcastGroupApi, hashtagApi } from '../../../services/contactApi';
import { useLanguage } from '../../../contexts/LanguageContext';

/**
 * 顯示 webhook 流程允許名單摘要 + 觸發 modal 的按鈕
 *
 * value: { contacts: [{id,name}], groups: [id], hashtags: [id] }
 */
const WebhookAccessControlField = ({ value, onClick, placeholder }) => {
  const { t } = useLanguage();

  // 為了在文字摘要中顯示「群組名」「標籤名」而不是 id，這裡 lazy 載入一次清單
  // 注意：modal 內部也會自行載入；這裡只用於 compact 顯示。
  const [groupsMap, setGroupsMap] = useState({}); // id -> name
  const [hashtagsMap, setHashtagsMap] = useState({}); // id -> name

  const groupIds = Array.isArray(value?.groups) ? value.groups : [];
  const hashtagIds = Array.isArray(value?.hashtags) ? value.hashtags : [];
  const contacts = Array.isArray(value?.contacts) ? value.contacts : [];
  const totalCount = groupIds.length + hashtagIds.length + contacts.length;

  useEffect(() => {
    let cancelled = false;
    // 只有當有需要顯示名稱、且尚未有對應名稱的 id 才載入
    const needGroupNames = groupIds.some((id) => !groupsMap[id]);
    const needHashtagNames = hashtagIds.some((id) => !hashtagsMap[id]);
    if (!needGroupNames && !needHashtagNames) return;

    (async () => {
      try {
        const tasks = [];
        if (needGroupNames) tasks.push(broadcastGroupApi.getGroups());
        else tasks.push(Promise.resolve(null));

        if (needHashtagNames) tasks.push(hashtagApi.getHashtags());
        else tasks.push(Promise.resolve(null));

        const [groupsRes, hashtagsRes] = await Promise.all(tasks);
        if (cancelled) return;

        if (groupsRes) {
          const groups = Array.isArray(groupsRes) ? groupsRes : groupsRes?.data || [];
          const map = { ...groupsMap };
          groups.forEach((g) => {
            map[g.id] = g.name;
          });
          setGroupsMap(map);
        }
        if (hashtagsRes) {
          const tags = Array.isArray(hashtagsRes) ? hashtagsRes : hashtagsRes?.data || [];
          const map = { ...hashtagsMap };
          tags.forEach((h) => {
            map[h.id] = h.name;
          });
          setHashtagsMap(map);
        }
      } catch (err) {
        console.error('WebhookAccessControlField 載入名稱失敗:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(groupIds), JSON.stringify(hashtagIds)]);

  // 生成 summary text + tooltip
  const summaryParts = [];
  const tooltipParts = [];

  if (groupIds.length > 0) {
    summaryParts.push(
      t('workflowDesigner.webhookAccess.summaryGroups', { count: groupIds.length })
    );
    tooltipParts.push(
      `${t('recipientSelector.tagLabels.group')}: ` +
        groupIds.map((id) => groupsMap[id] || id).join(', ')
    );
  }
  if (hashtagIds.length > 0) {
    summaryParts.push(
      t('workflowDesigner.webhookAccess.summaryHashtags', { count: hashtagIds.length })
    );
    tooltipParts.push(
      `${t('recipientSelector.tagLabels.hashtag')}: ` +
        hashtagIds.map((id) => hashtagsMap[id] || id).join(', ')
    );
  }
  if (contacts.length > 0) {
    summaryParts.push(
      t('workflowDesigner.webhookAccess.summaryContacts', { count: contacts.length })
    );
    tooltipParts.push(
      `${t('recipientSelector.contacts')}: ` +
        contacts.map((c) => c.name || c.id).join(', ')
    );
  }

  const displayText = summaryParts.join('、');
  const tooltip = tooltipParts.join('\n');

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          backgroundColor: totalCount > 0 ? '#fafafa' : '#fff',
          overflow: 'hidden',
        }}
      >
        {totalCount > 0 ? (
          <Tooltip title={tooltip} placement="topLeft">
            <div
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: 14,
                color: '#262626',
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
              onClick={onClick}
            >
              {displayText}
            </div>
          </Tooltip>
        ) : (
          <div
            style={{
              flex: 1,
              padding: '8px 12px',
              fontSize: 14,
              color: '#999',
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
            onClick={onClick}
          >
            {placeholder || t('workflowDesigner.webhookAccess.fieldPlaceholder')}
          </div>
        )}
        <Button
          type="link"
          size="small"
          onClick={onClick}
          style={{
            padding: '8px 12px',
            borderLeft: '1px solid #f0f0f0',
            backgroundColor: '#fafafa',
            flexShrink: 0,
            height: 'auto',
            borderRadius: 0,
          }}
        >
          {t('workflowDesigner.webhookAccess.fieldButton')}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(WebhookAccessControlField);
